import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PermissionInput, Scalars } from '@generated/base-types';
import { SidePanelComponent } from '@new/admin/side-panel/side-panel.component';
import { InteractiveRowDirective } from '@new/common/directives/row-interaction.directive';
import { CopyArrayPipe } from '@new/common/pipes/copy-array.pipe';
import { SearchInputComponent } from '@new/common/search-input/search-input.component';
import { FormState } from '@new/common/types/form-types';
import { RemoteData } from '@new/common/utils/remote-data';
import { isDefined } from '@new/common/utils/type-guards/is-defined';
import { TableWrapperComponent } from '@new/pools/pool/lib/components/man-table/man-table-wrapper/table-wrapper.component';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { Table, TableModule } from 'primeng/table';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs';
import {
  ActionMenuComponent,
  ActionMenuOption
} from '../../../common/action-menu/action-menu.component';
import { ModalService } from '../../../common/modal/modal.service';
import { FuzzySearchService } from '../../../common/services/fuzzy-search.service';
import { PermissionFormComponent } from '../../pools/pool/pool-permissions/permission-form/permission-form.component';
import { PoolPermissionsFragment } from '../../services/load-pool-permissions.fragments.generated';
import {
  CreateUserPermission,
  DeleteUserPermission,
  UpdateUserPermission
} from '../../state/user-permissions.actions';
import { UserPermissionsState } from '../../state/user-permissions.state';

@Component({
  templateUrl: './user-permissions.component.html',
  host: { class: 'page' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SearchInputComponent,
    TranslateModule,
    TableWrapperComponent,
    TableModule,
    ActionMenuComponent,
    SidePanelComponent,
    CommonModule,
    PermissionFormComponent,
    CopyArrayPipe,
    InteractiveRowDirective
  ]
})
export class UserPermissionsComponent implements OnDestroy {
  @ViewChild('table', { static: false })
  public table?: Table;
  @ViewChild(PermissionFormComponent)
  public formComponent: PermissionFormComponent;

  public readonly filterFields = ['pool.name'];
  public readonly userPermissions$: Observable<
    RemoteData<PoolPermissionsFragment[]>
  >;
  public readonly formState$: Observable<
    FormState<Partial<PoolPermissionsFragment>>
  >;
  public readonly menuOptions: ActionMenuOption[] = [
    {
      label: 'common.actions.edit',
      callback: (data: PoolPermissionsFragment) => this.onEdit(data)
    },
    {
      label: 'common.actions.delete',
      danger: true,
      callback: (data: PoolPermissionsFragment) => this.onDelete(data)
    }
  ];
  public userId$: Observable<Scalars['ID'] | undefined>;
  private searchStringSource = new BehaviorSubject<string>('');
  private searchString$ = this.searchStringSource.asObservable();
  private destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private modalService: ModalService,
    private fuzzySearchService: FuzzySearchService<PoolPermissionsFragment>
  ) {
    this.formState$ = this.formState();
    this.dismissSidebarOnSuccessfulMutation();
    this.userPermissions$ = combineLatest([
      this.store.select(UserPermissionsState.userPermissions),
      this.searchString$
    ]).pipe(
      map(([userPermissions, searchString]) => {
        const result = this.fuzzySearchService.search(
          userPermissions.data || [],
          searchString,
          this.filterFields
        );

        return { ...userPermissions, data: result };
      })
    );
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.userId$ = this.route.parent!.paramMap.pipe(
      map(params => params.get('id') ?? undefined)
    );
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSearch(value: string): void {
    this.searchStringSource.next(value);
  }

  public onSearchClear(): void {
    this.searchStringSource.next('');
  }

  public onNew(): void {
    this.routeToForm('new');
  }

  public onEdit({ id }: PoolPermissionsFragment): void {
    this.routeToForm(id);
  }

  public onUpdate(permissionInput: PermissionInput): void {
    const userPermissionInput: PermissionInput = {
      role: permissionInput.role,
      subjectId: permissionInput.subjectId,
      userId: permissionInput.userId,
      poolId: permissionInput.poolId
    };

    this.route.queryParams
      .pipe(
        map<{ editing?: string }, string | undefined>(
          params => params?.editing
        ),
        filter(isDefined),
        take(1),
        switchMap(editing => {
          return this.store.dispatch(
            new UpdateUserPermission(editing, userPermissionInput)
          );
        })
      )
      .subscribe();
  }

  public onCreate(permissionInput: PermissionInput): void {
    this.store.dispatch(new CreateUserPermission(permissionInput));
  }

  public onClose(): void {
    this.routeToForm();
  }

  public async onDelete(permission: PoolPermissionsFragment): Promise<void> {
    const confirmDelete = await this.modalService.confirmDelete();
    if (confirmDelete) {
      this.store.dispatch(new DeleteUserPermission(permission.id));
    }
  }

  private formState(): Observable<FormState<Partial<PoolPermissionsFragment>>> {
    const editingParam$ = this.route.queryParamMap.pipe(
      map(params => params.get('editing') ?? undefined)
    );
    const disabled$ = this.store
      .select(UserPermissionsState.mutationRequestState)
      .pipe(
        map(requestState => requestState === 'loading'),
        distinctUntilChanged()
      );

    return combineLatest([editingParam$, disabled$]).pipe(
      switchMap(([editingParam, disabled]) => {
        const show: boolean = editingParam !== undefined;
        const id = show && editingParam !== 'new' ? editingParam : undefined;

        return this.userPermissions$.pipe(
          filter(({ requestState }) => requestState === 'success'),
          take(1),
          map(({ data }) =>
            id === undefined ? undefined : data?.find(a => a.id === id)
          ),
          map(data => ({ show, disabled, data }))
        );
      })
    );
  }

  private dismissSidebarOnSuccessfulMutation(): void {
    this.store
      .select(UserPermissionsState.mutationRequestState)
      .pipe(
        pairwise(),
        filter(
          ([lastState, currentState]) =>
            lastState === 'loading' && currentState === 'success'
        ),
        tap({ next: () => this.onClose() }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private routeToForm(editing?: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { editing },
      queryParamsHandling: 'merge'
    });
  }
}
